import { Dialog } from '@angular/cdk/dialog';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { firstValueFrom } from 'rxjs';
import {
  StaffQrPrintCopiesDialogComponent,
  type StaffQrPrintCopiesDialogData,
} from '../scan/staff-qr-print-copies-dialog.component';
import { buildStaffQrLabelText } from './staff-qr-label-type.util';

export interface PrintStaffClientQrOptions {
  userId: string;
  lastName?: string;
  printer: NiimbotPrinterService;
  translate: TranslateService;
  notification: NotSystemNotificationService;
  dialog: Dialog;
}

async function promptLabelCopies(
  dialog: Dialog,
  defaultCopies: number,
): Promise<number | null> {
  const ref = dialog.open<number | undefined, StaffQrPrintCopiesDialogData>(
    StaffQrPrintCopiesDialogComponent,
    {
      data: { defaultCopies },
    },
  );
  const result = await firstValueFrom(ref.closed);
  if (result === undefined) {
    return null;
  }
  return result;
}

async function copyUserIdToClipboard(
  userId: string,
  translate: TranslateService,
  notification: NotSystemNotificationService,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(userId);
  } catch {
    notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [translate.instant('common.clipboardCopyError')],
    });
    return;
  }

  notification.addToQueue({
    id: Date.now().toString(),
    type: 'success',
    text: [translate.instant('admin.moverInventory.printQrCopied')],
  });
}

export async function printStaffClientQr(
  options: PrintStaffClientQrOptions,
): Promise<void> {
  const userId = options.userId.trim();
  if (!userId) {
    return;
  }

  if (!Capacitor.isNativePlatform()) {
    await copyUserIdToClipboard(
      userId,
      options.translate,
      options.notification,
    );
    return;
  }

  const defaultCopies = options.printer.getSavedLabelsPerBatch();
  const copies = await promptLabelCopies(options.dialog, defaultCopies);
  if (copies === null) {
    return;
  }

  options.printer.saveLabelsPerBatch(copies);

  const label = buildStaffQrLabelText(
    options.translate,
    'client',
    options.lastName,
  );

  try {
    await options.printer.printQrCodeWithSavedLabelPreset(
      userId,
      copies,
      label,
    );
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        options.translate.instant('admin.moverInventory.printQrSuccess'),
      ],
    });
  } catch {
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [options.translate.instant('admin.moverInventory.printQrError')],
    });
  }
}
